import { InputNumber } from "@/components/InputNumber";
import { datePickerClearableProps, datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { validations } from "@/utils/validationUtils";
import { Card, Col, Collapse, CollapseProps, DatePicker, Form, Row, Space } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration } from "../../../../../../common/api/queries";
import ActionButton from "../../../../../../common/components/buttons/ActionButton";
import HiddenInput from "../../../../../../common/components/form/components/HiddenInput";
import { rowGutter } from "../../../../../../common/constants";
import { Client } from "../../../../../client/types";
import { EnumerationType } from "../../../../../enumerations/enums";
import { InsuranceType } from "../../../../enums";
import { CreateUpdateInsurance, CreateUpdateInsuranceContract, InsuranceContract } from "../../../../types";
import { isOtherVehicleInsurance } from "../../../../utils";
import GenericInsuranceFormPart from "../parts/GenericInsuranceFormPart";
import { LifeInsuranceFormPart } from "../parts/LifeInsuranceFormPart";
import RealtyInsuranceFormPart from "../parts/RealtyInsuranceFormPart";
import TravelInsuranceFormPart from "../parts/TravelInsuranceFormPart";
import { OtherVehicleInsuranceFormPart } from "../parts/vehicle/OtherVehicleInsuranceFormPart";
import { VehicleInsuranceFormPart } from "../parts/vehicle/VehicleInsuranceFormPart";

const colSpan = 4;

interface Props {
  initialContract?: InsuranceContract;
  clients: (Partial<Client> | undefined)[];
  insurancesCount: number;
  onInsurancesCountChange: (count: number) => void;
  onAnnualPremiumChange: () => void;
  onPartialPremiumChange: () => void;
}

export const InsuranceContractFormInsurancesSection = ({
  clients,
  insurancesCount,
  initialContract,
  onInsurancesCountChange,
  onAnnualPremiumChange,
  onPartialPremiumChange,
}: Props) => {
  const { t } = useTranslation();

  const productEnums = useEnumeration(EnumerationType.PRODUCTS);

  const form = Form.useFormInstance<CreateUpdateInsuranceContract>();

  const allInsurancesKeys = [...Array(insurancesCount)].map((_, index) => index.toString());

  const [openedInsurancesKeys, setOpenedInsurancesKeys] = useState<string[]>(allInsurancesKeys);

  const effectiveBeginningDate = Form.useWatch("effectiveBeginningDate", form) as string | undefined;
  const effectiveEndDate = Form.useWatch("effectiveEndDate", form);
  const insurances = Form.useWatch(["insurances"], form) as CreateUpdateInsurance[] | undefined;

  const handleInsuranceAdd = () => {
    const contract = form.getFieldsValue([["insurances"], ["productId"]]);
    const insurances = [...(contract.insurances || [])];
    insurances[insurances.length] = {
      ...insurances[insurances.length],
      type: productEnums.find((product) => product.id === contract.productId)?.insuranceType,
    } as CreateUpdateInsurance[];
    form.setFieldsValue({ insurances });
    onInsurancesCountChange(insurancesCount + 1);

    setOpenedInsurancesKeys([...openedInsurancesKeys, insurancesCount.toString()]);
  };

  const handleInsuranceDelete = (index: number) => {
    const updatedInsurances = [...(form.getFieldValue(["insurances"]) || [])];

    updatedInsurances.splice(index, 1);
    if (updatedInsurances.length === 1) {
      updatedInsurances[0] = {
        ...updatedInsurances[0],
        contractEntryDate: undefined,
        contractWithdrawalDate: undefined,
        annualPremium: undefined,
        partialPremium: undefined,
      };
    }
    form.setFieldsValue({ insurances: updatedInsurances as CreateUpdateInsurance[] });

    onInsurancesCountChange(insurancesCount - 1);
    onAnnualPremiumChange();
    onPartialPremiumChange();
  };

  const resolveEntryDateLabel = (type?: InsuranceType) => {
    return type === InsuranceType.LIFE
      ? t("contract.attrs.insurances.contractEntryDateLife")
      : t("contract.attrs.insurances.contractEntryDate");
  };

  const resolveWithdrawalDateLabel = (type?: InsuranceType) => {
    return type === InsuranceType.LIFE
      ? t("contract.attrs.insurances.contractWithdrawalDateLife")
      : t("contract.attrs.insurances.contractWithdrawalDate");
  };

  const getInsuranceFormDataByType = (index: number, type?: InsuranceType) => {
    switch (type) {
      case InsuranceType.MTPL:
      case InsuranceType.CRASH:
      case InsuranceType.GAP:
      case InsuranceType.PAS:
        return <VehicleInsuranceFormPart index={index} clients={clients} type={type} />;
      case InsuranceType.VEHICLE:
        const insurance = initialContract?.insurances[index];

        return (
          <OtherVehicleInsuranceFormPart
            index={index}
            clients={clients}
            vehicle={insurance && isOtherVehicleInsurance(insurance) ? insurance.vehicle : undefined}
          />
        );
      case InsuranceType.REALTY:
        return <RealtyInsuranceFormPart index={index} clients={clients} />;
      case InsuranceType.TRAVEL:
        return <TravelInsuranceFormPart index={index} clients={clients} />;
      case InsuranceType.LIFE:
        return <LifeInsuranceFormPart index={index} clients={clients} />;
      case InsuranceType.GENERIC:
        return <GenericInsuranceFormPart index={index} clients={clients} />;
      default:
        return <span className="sub-header-info dashed">{t("contract.helpers.insuranceProductPlaceholder")}</span>;
    }
  };

  const getItems = (insurancesCount: number) => {
    const items: CollapseProps["items"] = [];

    [...Array(insurancesCount)].forEach((_, index) => {
      items.push({
        key: index,
        forceRender: true,
        label: (
          <b>
            {t("contract.attrs.insurances._label")} {index + 1}
          </b>
        ),
        extra: (
          <span onClick={(event) => event.stopPropagation()}>
            <ActionButton icon="minus" disabled={insurancesCount === 1} onClick={() => handleInsuranceDelete(index)}>
              {t("contract.actions.deleteInsurance")}
            </ActionButton>
          </span>
        ),
        children: (
          <>
            <HiddenInput name={["insurances", index, "id"]} />
            <HiddenInput name={["insurances", index, "optimisticLockVersion"]} />
            <HiddenInput name={["insurances", index, "type"]} />

            {insurancesCount > 1 && (
              <Row gutter={rowGutter}>
                <Col span={colSpan}>
                  <Form.Item
                    name={["insurances", index, "contractEntryDate"]}
                    label={resolveEntryDateLabel(insurances?.[index]?.type)}
                    rules={[
                      validations.notNull,
                      effectiveBeginningDate
                        ? validations.notBefore(effectiveBeginningDate, t("contract.attrs.effectiveBeginningDate"))
                        : validations.none,
                      effectiveEndDate
                        ? validations.notAfter(effectiveEndDate, t("contract.attrs.effectiveEndDate"))
                        : validations.none,
                    ]}
                    {...datePickerFormItemProps}
                  >
                    <DatePicker
                      {...datePickerStandardProps}
                      minDate={effectiveBeginningDate ? dayjs(effectiveBeginningDate) : undefined}
                      maxDate={effectiveEndDate ? dayjs(effectiveEndDate) : undefined}
                    />
                  </Form.Item>
                </Col>

                <Col span={colSpan}>
                  <Form.Item
                    name={["insurances", index, "contractWithdrawalDate"]}
                    label={resolveWithdrawalDateLabel(insurances?.[index]?.type)}
                    rules={[
                      insurances?.[index]?.contractEntryDate
                        ? validations.notBefore(
                            insurances?.[index]?.contractEntryDate,
                            resolveEntryDateLabel(insurances?.[index]?.type),
                          )
                        : effectiveBeginningDate
                          ? validations.notBefore(effectiveBeginningDate, t("contract.attrs.effectiveBeginningDate"))
                          : validations.none,
                      effectiveEndDate
                        ? validations.notAfter(effectiveEndDate, t("contract.attrs.effectiveEndDate"))
                        : validations.none,
                    ]}
                    {...datePickerFormItemProps}
                  >
                    <DatePicker
                      {...datePickerClearableProps}
                      minDate={
                        insurances?.[index]?.contractEntryDate
                          ? dayjs(insurances?.[index]?.contractEntryDate)
                          : effectiveBeginningDate
                            ? dayjs(effectiveBeginningDate)
                            : undefined
                      }
                      maxDate={effectiveEndDate ? dayjs(effectiveEndDate) : undefined}
                    />
                  </Form.Item>
                </Col>

                <Col span={colSpan}>
                  <Form.Item
                    name={["insurances", index, "annualPremium"]}
                    label={t("contract.attrs.insurances.annualPremium")}
                    rules={[validations.notNull, validations.minNumber(0)]}
                  >
                    <InputNumber type="decimal" addon="euro" onChange={onAnnualPremiumChange} />
                  </Form.Item>
                </Col>

                <Col span={colSpan}>
                  <Form.Item
                    name={["insurances", index, "partialPremium"]}
                    label={t("contract.attrs.insurances.partialPremium")}
                    rules={[
                      validations.notNull,
                      validations.minNumber(0),
                      validations.maxNumber(
                        insurances?.[index]?.annualPremium,
                        t("contract.attrs.insurances.annualPremium"),
                      ),
                    ]}
                  >
                    <InputNumber type="decimal" addon="euro" onChange={onPartialPremiumChange} />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {getInsuranceFormDataByType(index, insurances?.[index]?.type)}
          </>
        ),
      });
    });

    return items;
  };

  return (
    <Card
      type="inner"
      className="card-box card-box--inner-extra"
      title={t("contract.sections.insurances")}
      extra={
        <Space>
          <ActionButton icon="arrows" onClick={() => setOpenedInsurancesKeys(allInsurancesKeys)}>
            {t("common.openAll")}
          </ActionButton>
          <ActionButton icon="shrink" onClick={() => setOpenedInsurancesKeys([])}>
            {t("common.closeAll")}
          </ActionButton>
        </Space>
      }
    >
      <Collapse
        activeKey={openedInsurancesKeys}
        onChange={(key) => setOpenedInsurancesKeys(key)}
        items={getItems(insurancesCount)}
      />

      <ActionButton icon="plus" className="margin-top-medium" onClick={handleInsuranceAdd}>
        {t("contract.actions.addInsurance")}
      </ActionButton>
    </Card>
  );
};
